import { useMemo } from 'react';

import { EventInfo } from '@/api/events/types/types';
import { usePlayerPropsStore } from '@/feature/betslip-pickem/hooks/use-player-props-store';
import { PlayerWithTeam, PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { eventUtils } from '@/feature/betslip-pickem/utils/event-utils';
import { useGetOrderedLeagues } from '@/hooks/use-fantasy-league-configs';
import { League, ProjectionType } from '@/types/api.generated';
import { gameUtils } from '@/utils/games';
import { sortPlayersByAwayTeam, sortPlayersByEventStartDate, sortPlayersBySpecialPicks } from '@/utils/sortPlayers';

import { FilterType, Filters, getPositionFilters } from '../utils/filters';
import { POPULAR_PLAYERS_FILTER } from '../utils/players';
import { usePlayerFilters } from './use-player-filters';
import { usePopularPlayers } from './use-popular-players';

/**
 * Hook that sorts and filters the players based on the selected filters.
 * It will also compute the popular players.
 *
 * If there are 'Popular' players and the selected filter is 'Popular' it will return those players.
 * Otherwise this returns the players from the provided events, using the selected filter.
 */
export function useSortedLobbyPlayers({
    sortedEvents,
    league,
    filterType,
}: {
    sortedEvents: EventInfo[];
    league: League;
    filterType: FilterType;
}): PlayerWithTeamAndEvent[] {
    // Compute the popular players based on the sorted events
    const popularProjectionsPlayers = usePopularProjectionsPlayers(sortedEvents);

    const { leagues } = useGetOrderedLeagues();
    const positionFilters = useMemo(() => getPositionFilters(league, leagues), [league, leagues]);
    const { playerPositionFilter, playerProjectionFilter } = usePlayerFilters();

    const sortedPlayers: PlayerWithTeam[] = useMemo(() => {
        if (filterType === Filters.Position) {
            return sortedEvents.flatMap(event => {
                return gameUtils.filterAndSortPlayers(event, positionFilters, playerPositionFilter);
            });
        }

        if (
            filterType === Filters.Projection &&
            playerProjectionFilter !== undefined &&
            playerProjectionFilter !== POPULAR_PLAYERS_FILTER
        ) {
            return gameUtils.filterAndSortPlayersOnProjectionView(sortedEvents, playerProjectionFilter);
        }
        if (filterType === Filters.Projection && playerProjectionFilter === POPULAR_PLAYERS_FILTER) {
            return popularProjectionsPlayers || [];
        }
        return [];
    }, [
        filterType,
        playerProjectionFilter,
        sortedEvents,
        positionFilters,
        playerPositionFilter,
        popularProjectionsPlayers,
    ]);

    //Map players back to include their events, as the sortedPlayers only contains players
    const sortedPlayersWithEvents: PlayerWithTeamAndEvent[] = useMemo(() => {
        return sortedPlayers
            .map(player => {
                const event = sortedEvents.find(e => eventUtils.eventContainsPlayer(e, player.id));
                if (!event) {
                    console.warn('Player without event found:', player.lastName, '???');
                    return null;
                }
                return {
                    ...player,
                    event,
                };
            })
            .filter(Boolean);
    }, [sortedPlayers, sortedEvents]);

    return sortedPlayersWithEvents;
}

/**
 * Hook that computes the 'Popular' players for a given list of events
 * It will compute this list based on which player is also available in the "popular player | top 10 query",
 * or if the player has a discounted projection
 *
 * @returns popularProjectionsPlayers - list of popular players or undefined if the data is still loading
 */
export const usePopularProjectionsPlayers = (sortedEvents: EventInfo[]) => {
    const { popularPlayers, fetching } = usePopularPlayers({
        //those are already fetched on the lobby load
        requestPolicy: 'cache-only',
    });
    const playerMarkets = usePlayerPropsStore(state => state.playerMarkets);

    const popularProjectionsPlayers = useMemo(() => {
        if (fetching || sortedEvents.length === 0) {
            return undefined;
        }

        // Get all players with event times attached
        const playersWithEventTimes = gameUtils.extractAllPlayersFromEvents(sortedEvents);
        const loading = playersWithEventTimes.map(it => it.id).every(it => playerMarkets[it] === undefined);
        if (loading) {
            return undefined;
        }
        // Get all popular players with event times attached
        const popularPlayersWithEventTimes = popularPlayers
            .map(player => {
                return playersWithEventTimes.find(p => p.id === player.id);
            })
            .filter(Boolean);

        // Filter all players with discounted projections
        const specialPlayersWithEventTimes = playersWithEventTimes.filter(player => {
            const playerProjections = playerMarkets[player.id] || [];
            return playerProjections.some(
                projection => projection.type === ProjectionType.Special || projection.type === ProjectionType.FreePick
            );
        });

        const mergedList = [
            ...new Map(
                [...popularPlayersWithEventTimes, ...specialPlayersWithEventTimes].map(item => [item.id, item])
            ).values(),
        ];
        return mergedList.sort(
            (p1, p2) =>
                sortPlayersBySpecialPicks(p1, p2) ||
                sortPlayersByEventStartDate(p1, p2) ||
                sortPlayersByAwayTeam(p1, p2)
        );
    }, [fetching, playerMarkets, popularPlayers, sortedEvents]);

    return popularProjectionsPlayers;
};
